import React, { useState, useEffect } from 'react'

interface ImageProps {
  className?: string
  src: string
  srcApi?: string
  alt?: string
  height?: number | string
  width?: number | string
  id?: string
  fallbackSrc?: string
  fallbackWidth?: number | string
  fallbackHeight?: number | string
  loading?: 'lazy' | 'eager'
  onLoad?: () => void
  onError?: () => void
  style?: React.CSSProperties
  isLandscape?: boolean
  isSquare?: boolean
  forceWidth?: number | string
}

const ImageWithBasePath: React.FC<ImageProps> = ({
  className,
  src,
  srcApi,
  alt = '',
  height,
  width,
  id,
  fallbackSrc = '/assets/img/placeholder.jpg',
  fallbackWidth,
  fallbackHeight,
  loading = 'lazy',
  onLoad,
  onError,
  style,
  isLandscape = false,
  isSquare = false,
  forceWidth = 0,
  ...props
}) => {
  const [imgSrc, setImgSrc] = useState<string>(() => {
    // Compute a safe initial src to avoid empty-string warnings before first effect
    if (srcApi) {
      return `${process.env.REACT_APP_BASE_URL_API}/public/storage/${srcApi}`
    }
    if (src) {
      const isAbsoluteOrData =
        src.startsWith('http') ||
        src.startsWith('data:') ||
        src.startsWith('/') ||
        src.startsWith('blob:')
      return isAbsoluteOrData ? src : `/${src}`
    }
    return fallbackSrc || ''
  })
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [hasError, setHasError] = useState<boolean>(false)

  useEffect(() => {
    // Reset states when src or srcApi changes
    setIsLoading(true)
    setHasError(false)

    // Determine the image source
    const fullSrc = srcApi
      ? `${process.env.REACT_APP_BASE_URL_API}/public/storage/${srcApi}`
      : src.startsWith('http') ||
        src.startsWith('data:') ||
        src.startsWith('/') ||
        src.startsWith('blob:')
      ? src
      : `/${src}`

    setImgSrc(fullSrc)
  }, [src, srcApi])

  const handleLoad = () => {
    setIsLoading(false)
    setHasError(false)
    onLoad?.()
  }

  const handleError = () => {
    setIsLoading(false)
    setHasError(true)

    // Try fallback image if available and different from current
    if (fallbackSrc && fallbackSrc !== imgSrc) {
      setImgSrc(fallbackSrc)
      setIsLoading(true)
      setHasError(false)
    } else {
      onError?.()
    }
  }

  const skeletonStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: '#f0f0f0',
    borderRadius: '4px',
    animation: 'imagePulse 1.5s ease-in-out infinite',
  }

  const errorPlaceholderStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: fallbackWidth || '100%',
    height: fallbackHeight || '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f8f9fa',
    color: '#6c757d',
    fontSize: '12px',
    borderRadius: '4px',
  }

  // Container style for different aspect ratios
  const getContainerStyle = (): React.CSSProperties => {
    if (isSquare) {
      return {
        position: 'relative',
        display: 'inline-block',
        width: width || '100%',
        aspectRatio: '1/1',
        overflow: 'hidden',
      }
    }
    if (isLandscape) {
      return {
        position: 'relative',
        display: 'inline-block',
        width: width || '100%',
        aspectRatio: '16/9',
        overflow: 'hidden',
      }
    }
    return {
      position: 'relative',
      display: 'inline-block',
      width: hasError && fallbackWidth ? fallbackWidth : forceWidth !== 0 ? forceWidth : undefined,
      height: hasError && fallbackHeight ? fallbackHeight : undefined,
    }
  }

  // Image style with object cover for aspect ratio containers
  const getImageStyle = (): React.CSSProperties => {
    if (isSquare || isLandscape) {
      return {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: 'center',
      }
    }
    return {}
  }

  return (
    <>
      <style>
        {`
          @keyframes imagePulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
      </style>
      <div
        className={`image-container ${isLoading ? 'loading' : ''} ${
          hasError ? 'error' : ''
        }`}
        style={getContainerStyle()}
      >
        {isLoading && <div className="image-skeleton" style={skeletonStyle} />}
        <img
          className={`${className || ''} ${
            isLoading ? 'opacity-0' : 'opacity-100'
          }`}
          src={imgSrc}
          alt={alt}
          height={isSquare || isLandscape ? undefined : height}
          width={isSquare || isLandscape ? undefined : width}
          id={id}
          loading={loading}
          onLoad={handleLoad}
          onError={handleError}
          style={{
            transition: 'opacity 0.3s ease-in-out',
            ...getImageStyle(),
            ...style,
          }}
          {...props}
        />
        {hasError && !isLoading && (
          <div
            className="image-error-placeholder"
            style={errorPlaceholderStyle}
          >
            <i className="isax isax-image" style={{ fontSize: '24px' }} />
          </div>
        )}
      </div>
    </>
  )
}

export default ImageWithBasePath
